import { CoreStore, handleStoreState } from "./RESTStore/CoreStore";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { projectsDataStore } from "./ProjectsStore";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";

export interface OnlineViewerOptions {
  archiveFileId?: number;
  compareWith?: number; // id of file to compare with
  workflowId?: number; // workflowId has to be passed when viewer is opened from workflow module
  fileName: string;
  callbackOnClose?: () => void; // function that is called when viewer is closed. Used in workflow module...
}

type FileToCompare = Pick<API_FileDataEntity, "id" | "state" | "name" | "archivId">;

export class OnlineViewerStore extends CoreStore {
  link = "";
  fileName = "";
  fileId: number = null;
  isVisible = false;
  options: OnlineViewerOptions = null;
  private callbackOnClose: () => void = null;

  constructor() {
    super();

    makeObservable(this, {
      link: observable,
      fileName: observable,
      fileId: observable,
      isVisible: observable,
      options: observable,
      clear: action.bound,
      toggleModal: action.bound,
      compareFiles: action.bound,
      getOptions: computed,
      fetch: action.bound,
      initViewer: action.bound,
    });
  }

  url(fileId: number) {
    return API_URL.VIEWER.getOnlineViewerUrl(projectsDataStore.projectId, fileId);
  }

  clear() {
    this.link = "";
    this.fileName = "";
    this.fileId = undefined;
    this.isVisible = false;
    this.callbackOnClose = undefined;
    this.options = undefined;
  }

  toggleModal() {
    this.isVisible = !this.isVisible;
    if (!this.isVisible) {
      if (this.callbackOnClose) {
        this.callbackOnClose();
      }
      this.reset();
    }
  }

  @handleStoreState()
  async fetch(fileId: number, options: OnlineViewerOptions) {
    try {
      this.isVisible = true;
      await this.initViewer(fileId, options);
      if (typeof options.callbackOnClose === "function") {
        this.callbackOnClose = options.callbackOnClose;
      }
    } catch (error) {
      this.reset();
      throw error;
    }
  }

  @handleStoreState()
  async initViewer(fileId: number, options: OnlineViewerOptions) {
    try {
      this.options = options;
      this.fileName = `"${options.fileName}"`;
      this.fileId = fileId;
      const params: { compareWith?: number; workflowId?: number } = {};
      if (options) {
        if (options.compareWith) {
          params.compareWith = options.compareWith;
        }
        if (options.workflowId) {
          params.workflowId = options.workflowId;
        }
      }
      let url: string;
      if (options.archiveFileId) {
        url = API_URL.VIEWER.getOnlineViewerUrlForArchivedFile(
          projectsDataStore.projectId,
          fileId,
          options.archiveFileId
        );
      } else {
        url = this.url(fileId);
      }
      const response = await API.getData(url, params);
      runInAction(() => {
        this.link = response.stringValue;
      });
    } catch (error) {
      throw error;
    }
  }

  async compareFiles(firstFile: FileToCompare, secondFile: FileToCompare) {
    try {
      let oldFile: FileToCompare, latestFile: FileToCompare;
      if (firstFile.state === "ARCHIVED" || firstFile.state === "DELETED") {
        oldFile = firstFile;
        latestFile = secondFile;
      } else if (secondFile.state === "ARCHIVED" || secondFile.state === "DELETED") {
        oldFile = secondFile;
        latestFile = firstFile;
      } else {
        throw new Error("None of the files is ARCHIVED");
      }
      this.fetch(latestFile.id, {
        // oldFile.id is for fallback. Normally back-end should return archivId
        compareWith: oldFile.archivId || oldFile.id,
        fileName: oldFile.name,
      });
    } catch (error) {
      throw error;
    }
  }

  get getOptions() {
    if (this.options) {
      return Object.assign({}, this.options);
    }
    return undefined;
  }

  // action.bound
  // _openWithWorkflow(fileId: number, workflowId: number) {
  //   return this.fetch(fileId, { workflowId: workflowId });
  // }
}

export const onlineViewerStore = new OnlineViewerStore();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).onlineViewerStore = onlineViewerStore;
